/**
 * @fileoverview Events Redux slice.
 * @summary Manages events state (list, current event, CRUD operations).
 *
 * @package KaraoNow
 * @module apps/front/src/store/slices/eventsSlice
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import type { ApiResponse } from '../../services/api.js'
import { apiRequest } from '../../services/api.js'
import { API_ENDPOINTS } from '../../config/api.js'
import { SubscriptionType } from '@karaonow/shared'

/**
 * @summary Event status type.
 * @description 'hidden' status is used to hide events from the main UI while keeping them in the quota count.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export type EventStatus = 'draft' | 'active' | 'completed' | 'cancelled' | 'hidden'

/**
 * @summary Event interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export interface Event {
  id: string
  name: string
  description?: string
  venueId?: string
  date: string | Date
  status: EventStatus
  qrCode: string
  planType?: SubscriptionType.FREE | SubscriptionType.ONE_SHOT | 'subscription'
  oneShotPurchasedAt?: string | Date
  oneShotExpiresAt?: string | Date
  createdAt: string | Date
  updatedAt: string | Date
}

/**
 * @summary Events state interface.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
interface EventsState {
  events: Event[]
  currentEvent: Event | null
  isLoading: boolean
  error: string | null
  filterStatus: EventStatus | 'all'
}

/**
 * @summary Initial events state.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const initialState: EventsState = {
  events: [],
  currentEvent: null,
  isLoading: false,
  error: null,
  filterStatus: 'all',
}

/**
 * @summary Fetch all events for the authenticated organizer.
 * @param {boolean} includeHidden - Whether to include hidden events (default: false).
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const fetchEvents = createAsyncThunk('events/fetchEvents', async (includeHidden: boolean = false) => {
  const url = includeHidden ? `${API_ENDPOINTS.EVENTS.LIST}?includeHidden=true` : API_ENDPOINTS.EVENTS.LIST
  const response = await apiRequest<{ events: Event[] }>({
    method: 'GET',
    url,
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch events')
  }

  return response.data!.events
})

/**
 * @summary Fetch a single event by ID.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const fetchEvent = createAsyncThunk('events/fetchEvent', async (eventId: string) => {
  const response = await apiRequest<{ event: Event }>({
    method: 'GET',
    url: API_ENDPOINTS.EVENTS.GET(eventId),
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch event')
  }

  return response.data!.event
})

/**
 * @summary Create a new event.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (eventData: {
    name: string
    description?: string
    venueId?: string
    date: string | Date
    status?: EventStatus
    skipQuotaCheck?: boolean // Allow bypassing quota check for ONE_SHOT events
  }, { rejectWithValue }) => {
    const response = await apiRequest<{ event: Event }>({
      method: 'POST',
      url: API_ENDPOINTS.EVENTS.CREATE,
      data: eventData,
    })

    if (!response.success) {
      // Use rejectWithValue to preserve error code and data
      return rejectWithValue({
        message: response.message || 'Failed to create event',
        code: response.code,
        data: (response as any).data,
        errors: (response as any).errors,
      } as ApiResponse)
    }

    return response.data!.event
  },
)

/**
 * @summary Update an existing event.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ eventId, eventData }: { eventId: string; eventData: Partial<Omit<Event, 'id'>> }, { rejectWithValue }) => {
    const response = await apiRequest<{ event: Event }>({
      method: 'PUT',
      url: API_ENDPOINTS.EVENTS.UPDATE(eventId),
      data: eventData,
    })

    if (!response.success) {
      // Use rejectWithValue to preserve error code and data
      return rejectWithValue({
        message: response.message || 'Failed to update event',
        code: response.code,
        data: (response as any).data,
        errors: (response as any).errors,
      } as ApiResponse)
    }

    return response.data!.event
  },
)

/**
 * @summary Hide an event (masquer).
 * @description Changes event status to 'hidden'. Only completed or cancelled events can be hidden.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const hideEvent = createAsyncThunk('events/hideEvent', async (eventId: string) => {
  const response = await apiRequest<{ event: Event }>({
    method: 'PUT',
    url: API_ENDPOINTS.EVENTS.HIDE(eventId),
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to hide event')
  }

  return response.data!.event
})

/**
 * @summary Delete an event (SuperAdmin only).
 * @description Permanently deletes an event. Only SuperAdmin can delete events.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export const deleteEvent = createAsyncThunk('events/deleteEvent', async (eventId: string) => {
  const response = await apiRequest({
    method: 'DELETE',
    url: API_ENDPOINTS.EVENTS.DELETE(eventId),
  })

  if (!response.success) {
    throw new Error(response.message || 'Failed to delete event')
  }

  return eventId
})

/**
 * @summary Events slice.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
const eventsSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilterStatus: (state, action: PayloadAction<EventStatus | 'all'>) => {
      state.filterStatus = action.payload
    },
    setCurrentEvent: (state, action: PayloadAction<Event | null>) => {
      state.currentEvent = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch events
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.isLoading = false
        state.events = action.payload
        state.error = null
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch events'
      })

    // Fetch single event
    builder
      .addCase(fetchEvent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchEvent.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentEvent = action.payload
        // Update in list if exists
        const index = state.events.findIndex((e) => e.id === action.payload.id)
        if (index >= 0) {
          state.events[index] = action.payload
        }
        state.error = null
      })
      .addCase(fetchEvent.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to fetch event'
      })

    // Create event
    builder
      .addCase(createEvent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.isLoading = false
        state.events.unshift(action.payload) // Add to beginning
        state.error = null
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to create event'
      })

    // Update event
    builder
      .addCase(updateEvent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.isLoading = false
        const index = state.events.findIndex((e) => e.id === action.payload.id)
        if (index >= 0) {
          state.events[index] = action.payload
        }
        if (state.currentEvent?.id === action.payload.id) {
          state.currentEvent = action.payload
        }
        state.error = null
      })
      .addCase(updateEvent.rejected, (state) => {
        state.isLoading = false
        // When using rejectWithValue, error details are in action.payload, not action.error
        // We don't set state.error here because errors are handled locally in components via formError
        // This prevents "Rejected" from appearing in the UI
        state.error = null
      })

    // Hide event
    builder
      .addCase(hideEvent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(hideEvent.fulfilled, (state, action) => {
        state.isLoading = false
        // Update event in list
        const index = state.events.findIndex((e) => e.id === action.payload.id)
        if (index >= 0) {
          state.events[index] = action.payload
        }
        // Remove from current view if hidden (unless includeHidden is true)
        if (action.payload.status === 'hidden') {
          state.events = state.events.filter((e) => e.id !== action.payload.id)
        }
        if (state.currentEvent?.id === action.payload.id) {
          state.currentEvent = action.payload
        }
        state.error = null
      })
      .addCase(hideEvent.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to hide event'
      })

    // Delete event (SuperAdmin only)
    builder
      .addCase(deleteEvent.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.isLoading = false
        state.events = state.events.filter((e) => e.id !== action.payload)
        if (state.currentEvent?.id === action.payload) {
          state.currentEvent = null
        }
        state.error = null
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || 'Failed to delete event'
      })
  },
})

export const { clearError, setFilterStatus, setCurrentEvent } = eventsSlice.actions
export default eventsSlice.reducer

